import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { useState, useEffect } from "react";
import { translations } from "../translations";

const FeatureBox = ({ icon, titleKey, descriptionKey }) => {
  const { t } = useLanguage();
  return (
    <motion.div
      className="text-center"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-xl shadow-slate-950/70">
        <div
          className="w-12 h-12 flex-shrink-0"
          style={{
            background: "linear-gradient(to right, #0ea5e9, #22d3d8, #34d399)",
            WebkitMaskImage: `url(${icon})`,
            maskImage: `url(${icon})`,
            WebkitMaskSize: "contain",
            maskSize: "contain",
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center"
          }}
          role="img"
          aria-label={t(titleKey)}
        />
      </div>
      <h3 className="text-xl font-semibold mb-2 text-slate-50">{t(titleKey)}</h3>
      <p className="text-slate-400 text-base">{t(descriptionKey)}</p>
    </motion.div>
  );
};

const PriceCard = ({ price, title, description }) => (
  <motion.div
    className="card-gradient p-8"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
  >
    <h3 className="text-4xl font-bold mb-4">{price}</h3>
    <h4 className="text-xl font-semibold mb-3">{title}</h4>
    <p className="leading-relaxed">{description}</p>
  </motion.div>
);

const SupportCard = ({ icon, title, description }) => (
  <motion.div
    className="card-gradient text-center p-8"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
  >
    <div className="text-white text-4xl font-bold mb-4">{icon}</div>
    <h4 className="text-xl font-semibold mb-3">{title}</h4>
    <p className="leading-relaxed">{description}</p>
  </motion.div>
);

const Hero = () => {
  const { t, language } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);

  // Optimized scroll handling with throttling
  const [scrollSpeed, setScrollSpeed] = useState(30);

  useEffect(() => {
    // Delay heavy animations until component is mounted
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Throttled scroll handler to reduce performance impact
  useEffect(() => {
    let lastScrollY = 0;
    let ticking = false;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollDelta = Math.abs(scrollY - lastScrollY);

      // Simplified speed calculation
      if (scrollDelta > 20) {
        setScrollSpeed(15);
      } else if (scrollDelta > 10) {
        setScrollSpeed(22);
      } else {
        setScrollSpeed(30);
      }

      lastScrollY = scrollY;
      ticking = false;
    };

    const requestScrollUpdate = () => {
      if (!ticking) {
        requestAnimationFrame(handleScroll);
        ticking = true;
      }
    };

    // Only add scroll listener after component is loaded
    if (isLoaded) {
      window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    }

    return () => window.removeEventListener('scroll', requestScrollUpdate);
  }, [isLoaded]);

  return (
    <div className="overflow-x-hidden w-full">
      {/* Main Hero Section – refreshed layout */}
      <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-24 pb-24">
        {/* background accents */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />
          <div className="absolute bottom-[-6rem] right-[-4rem] h-80 w-80 rounded-full bg-emerald-400/15 blur-3xl" />
          <div className="absolute inset-x-0 top-40 h-px bg-gradient-to-r from-transparent via-slate-600/40 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto flex max-w-6xl flex-col gap-14 px-6 lg:flex-row lg:items-center">
          {/* Left content */}
          <motion.div
            className="flex-1 space-y-8 text-center lg:text-left"
            initial={{ opacity: 0, x: -32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-900/70 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-300 shadow-[0_0_30px_rgba(56,189,248,0.3)]">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Live trading platform
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl font-bold leading-tight text-slate-50 sm:text-5xl md:text-6xl">
                {t("hero.title")}
              </h1>
              <p className="mx-auto max-w-xl text-base text-slate-300">
                Platform trading yang dirancang untuk kecepatan, keamanan, dan kemudahan.
                Kelola portofolio, analisis chart, dan eksekusi order dalam satu layar.
              </p>
            </div>

            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-start">
              <motion.a
                href="https://stockity1.id/auth?a=778ce754e42c#signup"
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 px-8 py-3 text-sm font-semibold text-slate-950 shadow-[0_12px_40px_rgba(56,189,248,0.55)] transition hover:brightness-110 sm:text-base"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
              >
                {t("hero.demo")} →
              </motion.a>
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById("fitur-platform");
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
                className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-900/70 px-6 py-2.5 text-sm font-medium text-slate-200 shadow-md transition hover:bg-slate-800"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Lihat fitur unggulan
              </button>
            </div>

            {/* stats row */}
            <div className="mt-2 grid max-w-xl grid-cols-3 gap-4 text-left text-xs text-slate-400 sm:text-sm">
              {[
                { label: "Trader aktif", value: "100K+" },
                { label: "Negara", value: "50+" },
                { label: "Uptime server", value: "99.9%" }
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-slate-700/70 bg-slate-900/70 px-4 py-3 shadow-sm"
                >
                  <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
                    {stat.label}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-slate-50">
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right content – phone + mini card */}
          <motion.div
            className="flex-1"
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="relative mx-auto flex max-w-sm items-center justify-center">
              {/* glow behind phone */}
              <div className="absolute inset-0 translate-y-6 rounded-full bg-sky-500/25 blur-3xl" />

              <div className="relative">
                <div className="absolute -left-10 top-6 rounded-2xl bg-slate-900/80 px-4 py-3 text-xs text-slate-100 shadow-xl shadow-slate-950/70">
                  <p className="mb-1 text-[11px] uppercase tracking-[0.16em] text-slate-400">
                    Signal harian
                  </p>
                  <p className="text-sm font-semibold text-emerald-400">
                    12 setup potensial
                  </p>
                </div>
                <div className="absolute -right-8 bottom-8 rounded-2xl bg-slate-900/80 px-4 py-3 text-xs text-slate-100 shadow-xl shadow-slate-950/70">
                  <p className="mb-1 text-[11px] uppercase tracking-[0.16em] text-slate-400">
                    P/L hari ini
                  </p>
                  <p className="text-sm font-semibold text-emerald-400">
                    +3.8%
                  </p>
                </div>

                <div className="relative w-full max-w-[260px] sm:max-w-[300px] lg:max-w-[340px]">
                  <img
                    src="/phone.png"
                    alt="Trading App"
                    className="w-full drop-shadow-[0_30px_80px_rgba(0,0,0,0.9)]"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Platform Features Section */}
      <section
        id="fitur-platform"
        className="relative w-full border-t border-slate-800/70 bg-gradient-to-br from-slate-950 via-blue-950/80 to-slate-950 py-20"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-0 h-56 w-56 -translate-x-1/2 rounded-full bg-sky-500/20 blur-3xl" />
          <div className="absolute bottom-[-4rem] left-10 h-64 w-64 rounded-full bg-cyan-400/15 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto flex max-w-6xl flex-col gap-12 px-6">
          <div className="flex flex-col items-center gap-4 text-center lg:items-start lg:text-left">
            <motion.span
              className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-900/70 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] shadow-[0_0_30px_rgba(56,189,248,0.3)]"
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400">
                Keunggulan platform
              </span>
            </motion.span>
            <motion.h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-50 mb-6 leading-tight"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Mengapa trader memilih{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400">
                Stockity?
              </span>
            </motion.h2>
            <motion.p
              className="max-w-2xl text-base text-slate-300"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Kombinasi eksekusi cepat, antarmuka yang intuitif, dan fleksibilitas strategi
              membuat Anda bisa fokus pada keputusan, bukan pada teknis platform.
            </motion.p>
          </div>

          {/* Features grid – reuse FeatureBox */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FeatureBox
              icon="/global.png"
              titleKey="hero.features.globalReach.title"
              descriptionKey="hero.features.globalReach.description"
            />
            <FeatureBox
              icon="/simple.png"
              titleKey="hero.features.simplicity.title"
              descriptionKey="hero.features.simplicity.description"
            />
            <FeatureBox
              icon="/flexible.png"
              titleKey="hero.features.flexibility.title"
              descriptionKey="hero.features.flexibility.description"
            />
          </div>
        </div>
      </section>

      {/* Metode pembayaran - nuansa cyan/teal agar beda dari section lain */}
      <section className="relative w-full border-t border-slate-800/70 bg-gradient-to-tr from-slate-950 via-cyan-950/50 to-slate-950 py-20">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/4 top-0 h-80 w-80 rounded-full bg-cyan-500/15 blur-3xl" />
          <div className="absolute right-1/4 bottom-0 h-72 w-72 rounded-full bg-sky-500/12 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto flex max-w-6xl flex-col items-center gap-12 px-6 text-center">
          <motion.div
            className="w-full max-w-4xl space-y-5"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-900/70 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] shadow-[0_0_30px_rgba(56,189,248,0.3)]">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400">
                Metode pembayaran
              </span>
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-50">
              {t("sections.easyDeposits")}
            </h2>
            <p className="mx-auto max-w-xl text-base text-slate-300">
              {t("sections.choosePayment")}
            </p>

            <motion.div
              className="mx-auto mt-6 w-full max-w-3xl rounded-3xl border border-slate-700/70 bg-slate-900/70 p-6 shadow-xl shadow-slate-950/70"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">
                  Didukung oleh
                </p>
                <span className="rounded-full border border-sky-500/30 bg-slate-800/80 px-3 py-1 text-xs font-semibold">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-cyan-400 to-emerald-400">
                    Bank &amp; e-wallet lokal
                  </span>
                </span>
              </div>

              <motion.div
                className="grid grid-cols-3 gap-4 sm:grid-cols-4"
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                {[
                  { src: "/logo/bca_logo.svg", alt: "BCA" },
                  { src: "/logo/mandiri_logo.svg", alt: "Mandiri" },
                  { src: "/logo/bni_logo.svg", alt: "BNI" },
                  { src: "/logo/bri_logo.svg", alt: "BRI" },
                  { src: "/logo/permata_logo.svg", alt: "Permata" },
                  { src: "/logo/dana_logo.svg", alt: "DANA" },
                  { src: "/logo/ovo_logo.svg", alt: "OVO" },
                  { src: "/logo/spey_logo.svg", alt: "ShopeePay" }
                ].map((item) => (
                  <motion.div
                    key={item.alt}
                    className="flex items-center justify-center rounded-2xl border border-slate-700/70 bg-slate-900/70 px-4 py-4 transition-all duration-300 hover:border-sky-400 hover:bg-slate-800/90 hover:shadow-[0_0_24px_rgba(56,189,248,0.25)]"
                    whileHover={{ y: -4, scale: 1.05 }}
                    transition={{ duration: 0.25 }}
                  >
                    <img
                      src={item.src}
                      alt={item.alt}
                      className="max-h-9 w-auto object-contain opacity-95"
                    />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <div className="mt-4 grid gap-4 text-left text-base text-slate-300 sm:grid-cols-2 mx-auto max-w-3xl">
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                <p>Deposit instan dengan konfirmasi otomatis langsung ke saldo akun trading Anda.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-sky-400" />
                <p>Dukungan bank lokal dan e-wallet utama di Indonesia.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-cyan-300" />
                <p>Riwayat transaksi yang transparan dan mudah dicek kapan saja.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                <p>Notifikasi real-time setiap deposit maupun penarikan berhasil.</p>
              </div>
            </div>

            <motion.a
              href="https://stockity1.id/auth?a=778ce754e42c#signup"
              className="mt-6 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 px-7 py-2.5 text-sm font-semibold text-slate-950 shadow-[0_12px_40px_rgba(56,189,248,0.55)] transition hover:brightness-110"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              {t("sections.createAccount")}
            </motion.a>
          </motion.div>
        </div>
      </section>

      <section className="relative py-20 px-6 overflow-hidden w-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="container mx-auto max-w-7xl w-full">
          {/* Centered Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.span
              className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-900/70 px-4 py-1 mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-sky-300 shadow-[0_0_30px_rgba(56,189,248,0.3)]"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              FITUR TRADING PREMIUM
            </motion.span>
            <motion.h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-50 mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Mulai Trading dengan{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400">
                Mudah
              </span>
            </motion.h2>
            <motion.p
              className="text-base text-slate-300 leading-relaxed"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Dapatkan akses ke tools trading terbaik dengan harga terjangkau dan dukungan 24/7
              untuk kesuksesan Anda.
            </motion.p>
          </div>

          {/* Bento-style asymmetric grid: 4 cols, varied row spans */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 auto-rows-fr">
            {/* $10,000 Demo - Tall card (2 col, 2 row) */}
            <motion.div
              className="group relative bg-slate-900/70 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/70 hover:border-sky-500/40 transition-all duration-500 cursor-pointer overflow-hidden md:col-span-2 lg:col-span-2 lg:row-span-2 min-h-[280px] lg:min-h-0 shadow-xl shadow-slate-950/70"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              {/* Background glow effect */}
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-sky-500/25 rounded-full blur-3xl"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-cyan-400 to-emerald-400 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                $10,000
              </motion.h3>
              <h4 className="text-2xl font-bold text-slate-50 mb-4 group-hover:text-sky-300 transition-colors duration-300">
                Akses Akun Demo
              </h4>
              <p className="text-base text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                Berlatih tanpa risiko dengan akun demo $10,000. Jelajahi strategi, pahami dinamika
                pasar, dan asah keterampilan Anda sebelum berinvestasi uang sungguhan.
              </p>

              {/* Floating particle */}
              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-emerald-400/70 rounded-full"
                animate={{
                  y: [-6, 6, -6],
                  opacity: [0.4, 1, 0.4]
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>

            {/* $10 Start */}
            <motion.div
              className="group relative bg-slate-900/70 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/70 hover:border-cyan-500/40 transition-all duration-500 cursor-pointer overflow-hidden lg:col-span-2 shadow-xl shadow-slate-950/70"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-cyan-500/25 rounded-full blur-3xl"
                animate={{
                  scale: [1.1, 0.9, 1.1],
                  opacity: [0.2, 0.5, 0.2]
                }}
                transition={{
                  duration: 4.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              >
                $10
              </motion.h3>
              <h4 className="text-2xl font-bold text-slate-50 mb-4 group-hover:text-cyan-300 transition-colors duration-300">
                Mulai Hanya dengan $10
              </h4>
              <p className="text-base text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                Mulai karir trading Anda dengan deposit minimal hanya $10—sempurna untuk belajar
                tanpa risiko finansial besar.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-cyan-400/70 rounded-full"
                animate={{
                  y: [4, -4, 4],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.7
                }}
              />
            </motion.div>

            {/* $1 Entry */}
            <motion.div
              className="group relative bg-slate-900/70 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/70 hover:border-sky-500/40 transition-all duration-500 cursor-pointer overflow-hidden lg:col-span-2 shadow-xl shadow-slate-950/70"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-sky-500/25 rounded-full blur-3xl"
                animate={{
                  scale: [0.9, 1.3, 0.9],
                  opacity: [0.3, 0.7, 0.3]
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 2.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              >
                $1
              </motion.h3>
              <h4 className="text-2xl font-bold text-slate-50 mb-4 group-hover:text-sky-300 transition-colors duration-300">
                Entry Rendah — Trade $1
              </h4>
              <p className="text-base text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                Mulai trading dengan minimal $1 per trade—ideal bagi pemula untuk berlatih dan
                membangun kepercayaan diri dengan terjangkau.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-emerald-400/70 rounded-full"
                animate={{
                  y: [-4, 8, -4],
                  opacity: [0.4, 1, 0.4]
                }}
                transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.2
                }}
              />
            </motion.div>

            {/* 24/7 Support */}
            <motion.div
              className="group relative bg-slate-900/70 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/70 hover:border-sky-500/40 transition-all duration-500 cursor-pointer overflow-hidden lg:col-span-2 shadow-xl shadow-slate-950/70"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.4, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-sky-500/25 rounded-full blur-3xl"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.4, 0.8, 0.4]
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3.7,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.5
                }}
              >
                24/7
              </motion.h3>
              <h4 className="text-2xl font-bold text-slate-50 mb-4 group-hover:text-sky-300 transition-colors duration-300">
                Dukungan 24/7
              </h4>
              <p className="text-base text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                Ada pertanyaan? Tim support ahli kami tersedia 24/7 melalui email atau live chat
                untuk membantu Anda kapan saja.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-sky-400/70 rounded-full"
                animate={{
                  y: [2, -6, 2],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{
                  duration: 2.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.3
                }}
              />
            </motion.div>

            {/* 30+ Tools */}
            <motion.div
              className="group relative bg-slate-900/70 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/70 hover:border-cyan-500/40 transition-all duration-500 cursor-pointer overflow-hidden lg:col-span-2 shadow-xl shadow-slate-950/70"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-cyan-500/25 rounded-full blur-3xl"
                animate={{
                  scale: [1.2, 0.8, 1.2],
                  opacity: [0.2, 0.6, 0.2]
                }}
                transition={{
                  duration: 4.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.8
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2
                }}
              >
                30+
              </motion.h3>
              <h4 className="text-2xl font-bold text-slate-50 mb-4 group-hover:text-cyan-300 transition-colors duration-300">
                30+ Alat Analisis Lanjutan
              </h4>
              <p className="text-base text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                Perkuat trading Anda dengan 30+ indikator dan alat charting lanjutan untuk membuat
                keputusan lebih cerdas berbasis data.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-emerald-400/70 rounded-full"
                animate={{
                  y: [-2, 4, -2],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.8
                }}
              />
            </motion.div>

            {/* Free Education - Full width bottom row */}
            <motion.div
              className="group relative bg-slate-900/70 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/70 hover:border-sky-500/40 transition-all duration-500 cursor-pointer overflow-hidden md:col-span-2 lg:col-span-4 shadow-xl shadow-slate-950/70"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.6, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-sky-500/25 rounded-full blur-3xl"
                animate={{
                  scale: [0.8, 1.4, 0.8],
                  opacity: [0.3, 0.9, 0.3]
                }}
                transition={{
                  duration: 4.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.8
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3.6,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.8
                }}
              >
                Free
              </motion.h3>
              <h4 className="text-2xl font-bold text-slate-50 mb-4 group-hover:text-sky-300 transition-colors duration-300">
                Edukasi Komprehensif Gratis
              </h4>
              <p className="text-base text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                Dapatkan akses gratis ke sumber daya edukasi premium. Pelajari strategi, asah
                keterampilan, dan maksimalkan potensi trading Anda.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-emerald-400/70 rounded-full"
                animate={{
                  y: [6, -2, 6],
                  opacity: [0.4, 1, 0.4]
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2.2
                }}
              />
            </motion.div>
          </div>
        </div>
      </section>


      {/* Penghargaan Section - nuansa emerald/teal agar beda dari section lain */}
      <section className="py-20 px-6 overflow-hidden relative w-full bg-gradient-to-bl from-slate-950 via-emerald-950/60 to-slate-950">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute right-0 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-emerald-500/10 blur-3xl" />
        </div>
        <div className="container mx-auto max-w-screen-xl px-6 w-full relative z-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Kiri - Award */}
            <div className="lg:col-span-6">
              <div className="relative flex h-[320px] w-full max-w-md items-center justify-center lg:justify-start">
                <motion.div
                  className="relative z-10"
                  initial={{ opacity: 0, scale: 0.95, y: 30 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.03, transition: { duration: 0.3 } }}
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-sky-500/20 rounded-full blur-3xl scale-150" />
                    <img
                      src="/award.webp"
                      alt="Penghargaan platform trading"
                      className="relative z-10 h-auto w-[320px] md:w-[360px] drop-shadow-2xl"
                    />
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Kanan - Konten */}
            <div className="lg:col-span-6 space-y-4 text-left">
              <motion.span
                className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-900/70 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-300 shadow-[0_0_30px_rgba(56,189,248,0.3)] w-fit"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Penghargaan
              </motion.span>
              <motion.h2
                className="text-4xl md:text-5xl lg:text-6xl font-bold  text-slate-50 leading-tight"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                viewport={{ once: true }}
              >
                Stockity Dipercaya Oleh{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400">
                  Profesional Trader!
                </span>
              </motion.h2>
              <motion.p
                className="text-base text-slate-300 leading-relaxed max-w-xl"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Dianugerahi sebagai salah satu platform trading paling terpercaya di Indonesia —
                mengutamakan keamanan, kecepatan, dan inovasi untuk trader.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section - Teks kiri, gambar kanan */}
      <section className="py-20 px-6 relative w-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="container mx-auto max-w-7xl w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Kiri - Konten teks */}
            <motion.div
              className="lg:col-span-6 space-y-8 order-1"
              initial={{ opacity: 0, y: 40, x: -30 }}
              whileInView={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <div className="space-y-4">
                <motion.span
                  className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-900/70 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-300 shadow-[0_0_30px_rgba(56,189,248,0.3)]"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  MOBILE APPLICATION
                </motion.span>
                <motion.h2
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-50 leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  Trading di{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400">
                    Mana Saja
                  </span>
                </motion.h2>
                <motion.p
                  className="text-base text-slate-300 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  {t("mobileApp.description")}
                </motion.p>
              </div>

              <div className="flex flex-col gap-8 pt-4">
                <motion.div
                  className="flex flex-wrap gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <motion.a
                    href="https://stockity1.id/auth?a=778ce754e42c#signup"
                    className="inline-block"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <img
                      src="/appstore.svg"
                      alt="Download on App Store"
                      className="h-[52px] drop-shadow-lg"
                    />
                  </motion.a>
                  <motion.a
                    href="https://stockity1.id/auth?a=778ce754e42c#signup"
                    className="inline-block"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <img
                      src="/google-play.svg"
                      alt="Get it on Google Play"
                      className="h-[52px] drop-shadow-lg"
                    />
                  </motion.a>
                </motion.div>

                <motion.div
                  className="flex flex-col gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <img
                    src="/qrcode.png"
                    alt="Download Android APK"
                    className="w-36 rounded-2xl border border-slate-700/70 p-2 bg-white/5 backdrop-blur-sm"
                  />
                  <span className="text-slate-400 text-sm font-medium">
                    {t("mobileApp.downloadAndroid")}
                  </span>
                  <motion.a
                    href="https://stockity1.id/auth?a=778ce754e42c#signup"
                    className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 px-8 py-3 text-sm font-semibold text-slate-950 shadow-[0_12px_40px_rgba(56,189,248,0.55)] transition hover:brightness-110 w-fit"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Mulai Trading →
                  </motion.a>
                </motion.div>
              </div>
            </motion.div>

            {/* Kanan - Gambar app */}
            <motion.div
              className="lg:col-span-6 relative order-2"
              initial={{ opacity: 0, scale: 0.98, x: 30 }}
              whileInView={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="absolute inset-0 bg-sky-500/10 rounded-3xl blur-3xl scale-110" />
              <img
                src="/trading-app.png"
                alt="Stockity Trading App"
                className="w-full max-w-[700px] mx-auto relative z-10 drop-shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hero;

